import React, { useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Avatar, AvatarFallback } from "@shared/ui/avatar";
import { CreditCard, Download, Eye } from "lucide-react";
import { MOCK_BILLS } from "../mockData";
import { useNavigate } from "react-router-dom";
import { useMultipleConsumerBills, useAllAccounts } from "../../../../../shared/hook";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@shared/ui/tooltip";
import { useDownloadBillTemplate, useViewBillTemplate } from "../../../../../shared/hook";
import { useToast } from "@shared/hooks/use-toast";
import { useTranslation } from "react-i18next";

export default function AllBills() {
  const { t } = useTranslation();
  const navigate=useNavigate();
  const downloadBillTemplate = useDownloadBillTemplate();
  const viewBillTemplate = useViewBillTemplate();
  const { toast } = useToast();

  const handleDownloadBill = (bill: any) => {
    downloadBillTemplate.mutate(
      {
        billId: bill.id || bill.invoiceNo, // Use the appropriate ID from your row data
      },
      {
        onSuccess: () => {
          toast({
            title: t("Success!"),
            description: t("Bill downloaded successfully"),
          });
        },
        onError: (error: any) => {
          console.error("Download failed:", error);
          toast({
            title: t("Download Failed"),
            description: t("Failed to download bill. Please try again."),
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleViewBill = (bill: any) => {
    viewBillTemplate.mutate(
      {
        billId: bill.id || bill.invoiceNo, 
      },
      {
        onSuccess: () => {
          toast({
            title: t("Success!"),
            description: t("Bill opened in new tab"),
          });
        },
        onError: (error: any) => {
          console.error("View failed:", error);
          toast({
            title: t("View Failed"),
            description: t("Failed to load bill. Please try again."),
            variant: "destructive",
          });
        },
      }
    );
  };

  const { consumerId, remoteConsumerNumber } = getLoginDataFromStorage();
  const remoteUtilityId = getRemoteUtilityId();

  const { data: allAccounts } = useAllAccounts({
    page: 1,
    limit: 1000,
    active_consumers: 1,
    remote_utility_id: remoteUtilityId,
    is_sub_account: 1,
    consumer_id: getLoginDataFromStorage().consumerId,
  });

  const allConsumerNos = useMemo(() => {
    const subNos = allAccounts?.results?.map((acc: any) => acc.consumerNo) || [];
    return subNos;
  }, [allAccounts, remoteConsumerNumber]);

  const { data: billsData } = useMultipleConsumerBills({
    remote_utility_id: remoteUtilityId,
    consumer_no: allConsumerNos,
    is_pagination_required: true,
    is_bill_summary: true
  });
  
  const billsColumns: TableColumn<any>[] = useMemo(() => [
    {
      key: "invoiceNo",
      header: t("Bill Id"),
      render: (val) => <span className="font-semibold text-xs text-gray-800">{val}</span>
    },
    {
      key: "remoteConsumerNumber",
      header: t("Account"),
      type:'badge'
    },
    {
      key: "billMonth",
      header: t("Period"),
      render: (val) => <span className="text-xs text-gray-600">{val}</span>
    },
    {
      key: "billAmount",
      header: t("Amount"),
      render: (val, row) => (
        <span className={`font-bold text-sm ${row.status === "Unpaid" ? "text-red-600" : "text-gray-900"}`}>
          {val}
        </span>
      )
    },
    {
      key: "billStatus",
      header: t("Status"),
      render: (val, row) => (
        <div className="flex gap-1.5 items-center">
          <Badge variant="secondary" className={`${val === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} border-0 px-2 py-0.5 text-xs rounded-full`}>
            {val}
          </Badge>
          {row.isOverdue && (
            <Badge variant="secondary" className="bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 text-xs rounded-full">
              {t("Overdue")}
            </Badge>
          )}
        </div>
      )
    },
    {
      key: "dueDate",
      header: t("Due Date"),
      render: (val, row) => (
        <span className={`text-xs ${row.status === "Unpaid" ? "text-red-600 font-medium" : "text-gray-500"}`}>
          {val}
        </span>
      )
    },
    {
      key: "consumptionDetails",
      header: t("Consumption"),
      render: (val: any) => {
        if (!val || val.length === 0) return <span className="text-xs text-gray-400">{t("N/A")}</span>;
        
        const total = val.reduce((acc: number, curr: any) => {
          const value = Object.values(curr)[0] as number;
          return acc + value;
        }, 0);

        return (
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <span className="text-xs text-gray-600 underline decoration-dashed cursor-help">
                  {total.toFixed(2)}
                </span>
              </TooltipTrigger>
              <TooltipContent className="bg-white border shadow-md text-gray-800 p-3 min-w-[150px]">
                <div className="space-y-2">
                  <div className="font-semibold text-xs border-b pb-1.5">{t("Consumption Details")}</div>
                  {val.map((item: any, idx: number) => {
                    const key = Object.keys(item)[0];
                    const value = item[key];
                    return (
                      <div key={idx} className="flex justify-between items-center text-xs gap-4">
                        <span className="text-gray-500">{key}</span>
                        <span className="font-medium text-gray-900">{value}</span>
                      </div>
                    );
                  })}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      }
    },
    {
      key: "actions",
      header: t("Actions"),
      render: (_, row) => (
        <div className="flex items-center gap-2">
          {row.status === "Unpaid" && (
            <Button variant="destructive" onClick={()=>navigate('/dashboard/billing/pay')}>
               <CreditCard className="w-3.5 h-3.5"/> {t("Pay")}
            </Button>
          )}
          <Button variant="outline" onClick={() => handleViewBill(row)} className="h-7 px-2.5 text-xs rounded-md font-semibold flex items-center gap-1 border-gray-200 text-gray-700">
             <Eye className="w-3.5 h-3.5"/> {t("View")}
          </Button>
          <Button variant="ghost" onClick={() => handleDownloadBill(row)} className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600">
             <Download className="w-4 h-4"/>
          </Button>
        </div>
      )
    }
  ], [t]);


  return (
    <div className="mt-4">
      
      <div>
        <GenericTable
          data={billsData?.billData} 
          columns={billsColumns}
          rowKey="id"
          selectable={false}
          searchPlaceholder={t("Search by bill ID or period...")}
          onSearch={() => {}}
          filters={
            <select className="border border-gray-300 rounded-md px-4 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 h-9">
              <option>{t("All Accounts")}</option>
            </select>
          }
        />
      </div>
    </div>
  );
}
